var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useRef } from 'react';
import { Button, Link, LoadingSpinner, Text, TextInput } from '@paypalcorp/pp-react';
import Profile from './Profile';
import { OTP_CODE, PUBLIC_CREDENTIAL, SEND_OTP } from '../common/views';
import { CONTINGENCY_TYPES_MAPPER } from '../common/contingencyTypes';
import { getProp, isMobile } from '../common/utils';
import { initializeAsync, initializeSync } from '../common/fn';
import { getCoBrand } from '../common/utils';
import { LOCAL, PREFERRED_LOGIN_TYPES, STAGE_URL } from './constants';
import { fetchPhoneCodeList, looksLikeAnEmail } from '../common/PhoneInput/utils';
import PhoneInput from '../common/PhoneInput';
const { OTP_LOGIN } = PREFERRED_LOGIN_TYPES;
export default ({ authEventHandler, authProviderContext = { fnData: {}, cookiedUser: {} }, challenges, csrf, forgetUser, isCookiedUser, eligibleFeatures, isOtpSent, localizedContent, logger, options = {}, publicCredential = '', setPublicCredential, sessionID, setView, verifyPrivateCredential, isEEACountry, countryCode, language, setLoginPreferenceInCookie, }) => {
    var _a;
    const [password, setPassword] = useState('');
    const [passwordFieldError, setPasswordFieldError] = useState('');
    const [errorCode, setErrorCode] = useState('');
    const [showPageLevelError, setShowPageLevelError] = useState('');
    const [showSpinner, setShowSpinner] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [renderEmailInput, setRenderEmailInput] = useState(false);
    const [publicCredentialFieldError, setLocalPublicCredentialFieldError] = useState('');
    const [localPublicCredential, setLocalPublicCredential] = useState(publicCredential);
    const [isPhoneNumber, setIsPhoneNumber] = useState(false);
    const [isEmailError, setIsEmailError] = useState(false);
    const [isPasswordError, setIsPasswordError] = useState(false);
    const [isCredentialsSubmitted, setIsCredentialsSubmitted] = useState(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState('US +1');
    const [phoneCodeList, setPhoneCodeList] = useState(null);
    const [citStartTime, setCitStartTime] = useState('');
    const coBrand = getCoBrand(countryCode);
    const locality = `${language}_${coBrand.toUpperCase()}`;
    const emailRef = useRef(null);
    const passwordRef = useRef(null);
    const handlePageClick = (event) => {
        if (emailRef.current && !emailRef.current.contains(event.target)) {
            setLocalPublicCredentialFieldError('');
        }
        if (passwordRef.current && !passwordRef.current.contains(event.target)) {
            setPasswordFieldError('');
        }
    };
    useEffect(() => {
        initializeAsync(authProviderContext.fnData);
        initializeSync(authProviderContext.fnData);
        setRenderEmailInput(true);
        const pwdInput = document.querySelector('#password');
        pwdInput && pwdInput.focus();
        document.addEventListener('click', handlePageClick);
        const prefillPasswordInput = document.querySelector('#prefillPwdInput');
        if (prefillPasswordInput && prefillPasswordInput.value && prefillPasswordInput.value.length) {
            logger.addToFlowContext({
                pwd_prefilled: 'true',
            });
            setPassword(prefillPasswordInput.value);
            prefillPasswordInput.remove();
        }
        authEventHandler({ event: 'pwdPageRendered' });
        setCitStartTime(Date.now());
        if (challenges) {
            logger.track(`pwd_rendered|${JSON.stringify({
                login_variant: 'secondary sms',
            })}`);
        }
        else {
            logger.track('pwd_rendered');
        }
        logger.flush({ csrf, sessionID });
        fetchPhoneCodeList(setPhoneCodeList);
        const showHidePasswordHandler = (e) => {
            const showHideBtn = document.querySelector('#btnShowHide');
            if (e.target.id !== 'btnShowHide' && e.target.id !== 'password') {
                showHideBtn && showHideBtn.classList.add('hide');
            }
        };
        document.body.addEventListener('click', showHidePasswordHandler);
        // on component unload, remove the listener
        return () => {
            document.body.removeEventListener('click', showHidePasswordHandler);
            document.body.removeEventListener('click', handlePageClick);
        };
    }, []);
    const showHideBtnHandler = (e) => {
        const showHideBtn = document.querySelector('#btnShowHide');
        if (e.target.value.length) {
            showHideBtn && showHideBtn.classList.remove('hide');
        }
        else {
            showHideBtn && showHideBtn.classList.add('hide');
        }
    };
    // Re-usable link component to be used the subsequent computePageLevelError method
    const LinkComponent = ({ clickHandler, contentKey }) => (React.createElement(Link, { href: "#", size: "sm", className: "boldText", onClick: clickHandler, "data-testid": "link-component" }, localizedContent(contentKey)));
    const computePageLevelError = () => {
        switch (errorCode) {
            case '1001':
                return (React.createElement(React.Fragment, null, localizedContent('tooManyAttempts', {
                    resetYourPasswordLink: (React.createElement(LinkComponent, { clickHandler: (e) => {
                            e.preventDefault();
                            logger.track('reset_password_link_clicked');
                            logger.flush({ csrf, sessionID });
                            if (options.signinUrl) {
                                location.assign(getPasswordRecoveryLink());
                                return;
                            }
                            authEventHandler({
                                event: 'lockedUserForgotPassword',
                            });
                        }, contentKey: "resetYourPassword" })),
                })));
            case '1004':
            case '1005':
                return (React.createElement(React.Fragment, null, localizedContent('riskDecline', {
                    contactUsLink: (React.createElement(LinkComponent, { clickHandler: (e) => {
                            e.preventDefault();
                            logger.track('cannot_login');
                            logger.flush({ csrf, sessionID });
                            authEventHandler({
                                event: 'cannotLogin',
                            });
                            location.assign(`/${coBrand}/cgi-bin/helpscr?cmd=_help`);
                        }, contentKey: "contactUs" })),
                })));
            default:
                if (challenges) {
                    return (React.createElement(React.Fragment, null, localizedContent('passwordPageLevelErrorWithOtp', {
                        loginWithOtpLink: (React.createElement(LinkComponent, { clickHandler: (e) => {
                                e.preventDefault();
                                logger.track('login_with_otp_link_clicked');
                                setView(isOtpSent ? OTP_CODE : SEND_OTP);
                            }, contentKey: "loginWithOtpText" })),
                    })));
                }
                else {
                    return React.createElement(React.Fragment, null, localizedContent('passwordPageLevelError'));
                }
        }
    };
    const getPasswordRecoveryLink = () => {
        return `${options.env === LOCAL ? STAGE_URL : ''}/authflow/password-recovery/?contextId=${authProviderContext.fnData.fnSessionId}&redirectUri=${encodeURIComponent(encodeURIComponent(options.signinUrl))}&sso=true`;
    };
    const handlePasswordSubmit = (e) => __awaiter(void 0, void 0, void 0, function* () {
        var _b, _c, _d, _e, _f;
        const startTime = Date.now();
        e.preventDefault();
        let isPhone;
        if (((_b = authProviderContext === null || authProviderContext === void 0 ? void 0 : authProviderContext.cookiedUser) === null || _b === void 0 ? void 0 : _b.maskedPhoneNumber) === localPublicCredential) {
            isPhone = true;
        }
        else {
            isPhone = !looksLikeAnEmail(localPublicCredential.trim());
        }
        setIsPhoneNumber(isPhone ? true : false);
        logger.addToFlowContext({
            pub_cred_type: isPhone ? 'phone' : 'email',
        });
        setIsPasswordError(false);
        // Prevent proceeding in case spinner is shown (implying a network request is underway)
        if (showSpinner) {
            return;
        }
        if (localPublicCredential.trim() === '') {
            setLocalPublicCredentialFieldError(localizedContent('required'));
            setIsEmailError(true);
            (_c = emailRef === null || emailRef === void 0 ? void 0 : emailRef.current) === null || _c === void 0 ? void 0 : _c.focus();
            return;
        }
        else if (password.trim() === '') {
            setPasswordFieldError(localizedContent('required'));
            setIsPasswordError(true);
            (_d = passwordRef === null || passwordRef === void 0 ? void 0 : passwordRef.current) === null || _d === void 0 ? void 0 : _d.focus();
            return;
        }
        setIsCredentialsSubmitted(true);
        if (!isPhone) {
            if (localPublicCredential.trim() === '') {
                setShowSpinner(false);
                setLocalPublicCredentialFieldError(localizedContent('required'));
                return;
            }
            if (!localPublicCredential.match(/\S+@\S+\.\S+/)) {
                logger.track('pc_invalid_email_entered');
                setShowSpinner(false);
                setLocalPublicCredentialFieldError(localizedContent('invalidEmailError'));
                setIsEmailError(true);
                (_e = emailRef === null || emailRef === void 0 ? void 0 : emailRef.current) === null || _e === void 0 ? void 0 : _e.focus();
                return;
            }
        }
        authEventHandler({ event: 'beginLogin', data: { loginMethod: 'password' } });
        setShowSpinner(true);
        logger.track('pwd_login_started');
        const response = yield verifyPrivateCredential({
            email: publicCredential,
            password,
            maskedPhoneNumber: (_f = authProviderContext === null || authProviderContext === void 0 ? void 0 : authProviderContext.cookiedUser) === null || _f === void 0 ? void 0 : _f.maskedPhoneNumber,
            targetApplication: {
                clientId: '',
                redirectUri: '',
                authProviderContextData: {
                    signinUrl: options.signinUrl,
                },
            },
        });
        // XO Lite apps do not have the ngRL JS SDK loaded,
        // hence a captcha response can go unhandled
        // the following is a poor way to handle it so that XO can fallback
        const extensions = getProp(response, 'extensions', null);
        if (!extensions) {
            authEventHandler({
                event: response === 'invalid_ctxId' ? 'invalidCtxId' : 'unknownResponse',
                data: {
                    response,
                },
            });
        }
        const accessToken = getProp(response, 'data.login.verifyPrivateCredential.accessToken');
        // In case of a contingency such as 2FA/SAFE/SCA stepup, the response will have a URL to redirect to
        const redirectUrl = getProp(response, 'data.login.verifyPrivateCredential.redirectUrl');
        if (redirectUrl) {
            let contingencyType;
            for (const key in CONTINGENCY_TYPES_MAPPER) {
                if (redirectUrl.includes(CONTINGENCY_TYPES_MAPPER[key])) {
                    contingencyType = key;
                }
            }
            logger.track('login_contingency'); // best effort
            logger.track(`cpl_pwd_login|${JSON.stringify({
                start: startTime,
                tt: Date.now() - startTime,
                login_experience: 'Pwd submit contingency',
            })}`);
            authEventHandler({ event: 'loginContingencyRedirect', data: { redirectUrl, accessToken, contingencyType } });
            if (!options.contingencyRedirect) {
                logger.track('login_contingency_redirect_identity');
                location.assign(redirectUrl);
            }
            return;
        }
        if (accessToken) {
            logger.track('pwd_login_succeeded');
            logger.track(`cpl_pwd_login|${JSON.stringify({
                start: startTime,
                tt: Date.now() - startTime,
                login_experience: 'Pwd submit',
            })}`);
            logger.track(`cpl_cit_password|${JSON.stringify({
                start: citStartTime,
                tt: Date.now() - citStartTime,
                login_experience: 'Login Form Submit',
            })}`);
            authEventHandler({ event: 'loggedIn', data: { accessToken, loginMethod: 'password' } });
        }
        else {
            logger.track('pwd_login_failed');
            logger.track(`cpl_pwd_login|${JSON.stringify({
                start: startTime,
                tt: Date.now() - startTime,
                login_experience: 'Pwd submit error',
            })}`);
            // If there has been an error, then it will have an error code
            // which will be used to determine the kind of error we want to show
            const errors = response.errors || [];
            setErrorCode((errors[0] &&
                errors[0].extensions &&
                Array.isArray(errors[0].extensions.details) &&
                errors[0].extensions.details[0] &&
                errors[0].extensions.details[0].issue) ||
                '');
            setPassword('');
            setShowPageLevelError(true);
            setShowSpinner(false);
            authEventHandler({
                event: 'loginFailed',
                data: { loginMethod: 'password' },
            });
        }
        logger.flush({ csrf, sessionID });
    });
    const handlePublicCredentialChange = (e) => {
        if (isCredentialsSubmitted === true) {
            if (e.target.value.match(/\S+@\S+\.\S+/)) {
                setLocalPublicCredentialFieldError('');
                setIsEmailError(false);
            }
            else if (e.target.value === '') {
                setLocalPublicCredentialFieldError(localizedContent('required'));
                setIsEmailError(true);
            }
            else {
                setLocalPublicCredentialFieldError(localizedContent('invalidEmailError'));
                setIsEmailError(true);
            }
        }
        setLocalPublicCredential(e.target.value);
        const isEmail = looksLikeAnEmail(e.target.value);
        setIsPhoneNumber(isEmail ? false : true);
    };
    const handleEmailInputFocus = () => {
        if (localPublicCredential.match(/\S+@\S+\.\S+/)) {
            setLocalPublicCredentialFieldError('');
            setIsEmailError(false);
        }
        else {
            setIsEmailError(true);
        }
    };
    const handlePasswordInputError = () => {
        if (isPasswordError) {
            setPasswordFieldError(localizedContent('required'));
        }
    };
    const emailInputStyles = {
        marginBottom: publicCredentialFieldError === '' ? '16px' : '',
        border: isEmailError === false ? '' : '.0625rem solid #d9360b',
    };
    const handleCountryCodeChange = (e) => {
        setSelectedCountryCode(e.target.value);
    };
    const inputField = isPhoneNumber ? (React.createElement("div", { style: { marginBottom: '16px' } },
        React.createElement(PhoneInput, { handleCountryCodeChange: handleCountryCodeChange, handlePublicCredentialChange: handlePublicCredentialChange, phoneCodeList: phoneCodeList, publicCredential: localPublicCredential, selectedCountryCode: selectedCountryCode, label: localizedContent('emailOrMobile') }))) : (React.createElement(TextInput, { "data-testid": "input-login-email", name: "login_email", id: "editable_email", type: "text", ref: emailRef, label: localizedContent('emailOrMobile'), error: !!isEmailError, errorText: publicCredentialFieldError, errorIconAriaLabel: !!publicCredentialFieldError, value: localPublicCredential, onChange: handlePublicCredentialChange, autoComplete: "username", tabIndex: "1", autoFocus: true, onFocus: handleEmailInputFocus, style: emailInputStyles }));
    return (React.createElement(React.Fragment, null,
        showSpinner && (React.createElement("div", { className: "overlay-spinner" },
            React.createElement(LoadingSpinner, { screenReaderText: localizedContent('loading'), size: "xl" }))),
        React.createElement("form", { className: !isCookiedUser ? 'slide-in' : '', onSubmit: handlePasswordSubmit, id: "passwordPageContainer", "data-testid": "password-page-container" },
            React.createElement("div", null,
                React.createElement(Text, { size: "xl2", className: "heading", id: "passwordPageHeading", "data-testid": "password-page-heading" }, localizedContent('genericHeading')),
                isEEACountry ? (React.createElement(Text, { className: "subHeading", id: "passwordPageSubHeading", "data-testid": "password-page-sub-heading", style: { margin: '0px 0 6%' } },
                    localizedContent('passwordSubTagLine'),
                    React.createElement(Link, { href: `/${coBrand}/webapps/mpp/ua/buyer-protection?locale.x=${locality}`, className: "secondaryLink", id: "passwordPageSubHeadingLink", "data-testid": "password-page-sub-heading-link", tabIndex: "1" }, localizedContent('seeTerms')))) : (React.createElement(Text, { className: "subHeading", id: "passwordPageSubHeading", "data-testid": "password-page-sub-heading", style: { margin: '0px 0 6%' } }, localizedContent('passwordSubHeading'))),
                showPageLevelError && (React.createElement("div", { role: "alert", className: "ulNotification-error", id: "passwordError", "data-testid": "password-error" }, computePageLevelError())),
                eligibleFeatures &&
                    Object.keys(eligibleFeatures).includes('showProfile') &&
                    ((_a = authProviderContext === null || authProviderContext === void 0 ? void 0 : authProviderContext.cookiedUser) === null || _a === void 0 ? void 0 : _a.email) ? (inputField) : (React.createElement(Profile, { publicCredential: publicCredential, handleClickChange: (e) => __awaiter(void 0, void 0, void 0, function* () {
                        e.preventDefault();
                        logger.deleteFromFlowContext('pwd_prefilled');
                        setPublicCredential('');
                        logger.track('change_user_clicked');
                        forgetUser({});
                        setView(PUBLIC_CREDENTIAL);
                    }), labelForChange: localizedContent('change') })),
                renderEmailInput && (React.createElement("span", { className: "hide" },
                    React.createElement("input", { name: "email", value: publicCredential, id: "email", "data-testid": "input-email", type: "email", readOnly: true, disabled: true }))),
                React.createElement(TextInput, { "data-testid": "input-current-password", autoComplete: "current-password", id: "password", name: "login_password", label: localizedContent('password'), type: showPassword ? 'text' : 'password', value: password, autoFocus: !isMobile(), ref: passwordRef, style: { border: isPasswordError === false ? '' : '.0625rem solid #d9360b' }, onFocus: handlePasswordInputError, rightIcon: React.createElement(Link, { href: "#", id: "btnShowHide", className: "hide", "data-testid": "btn-show-hide", onClick: (e) => {
                            e.preventDefault();
                            setShowPassword(!showPassword);
                            logger.track('pwd_toggle');
                        }, style: {
                            width: '20%',
                            fontSize: '13px',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                            padding: '3px',
                            textDecoration: 'none',
                            lineHeight: '1rem',
                        }, tabIndex: "3" }, showPassword ? localizedContent('hide') : localizedContent('show')), onChange: (e) => {
                        logger.addToFlowContext({ pwd_prefilled: 'updated' });
                        showHideBtnHandler(e);
                        if (passwordFieldError !== '')
                            setPasswordFieldError('');
                        setPassword(e.target.value);
                    }, onClick: (e) => {
                        showHideBtnHandler(e);
                    }, error: isPasswordError, errorText: passwordFieldError, tabIndex: "2" }),
                React.createElement("div", { style: { margin: '10px 0', textAlign: 'left' } },
                    React.createElement(Link, { href: "#", className: "secondaryLink", id: "linkForgotPassword", "data-testid": "link-forgot-password", onClick: (e) => {
                            e.preventDefault();
                            logger.track('pwr_clicked');
                            logger.flush({ csrf, sessionID });
                            if (options.signinUrl) {
                                location.assign(getPasswordRecoveryLink());
                                return;
                            }
                            authEventHandler({
                                event: 'userForgotPassword',
                            });
                        }, tabIndex: "4" }, localizedContent('forgotPassword')))),
            React.createElement("div", { style: { marginTop: '20px' } },
                React.createElement(Button, { type: "submit", style: { width: '100%' }, id: "buttonSubmitPassword", "data-testid": "button-submit-password", tabIndex: "5" }, localizedContent('logIn'))),
            challenges && (React.createElement(Button, { secondary: true, onClick: (e) => {
                    e.preventDefault();
                    logger.deleteFromFlowContext('pwd_prefilled');
                    logger.track('login_with_otp_clicked');
                    setLoginPreferenceInCookie({ csrf, sessionID, preference: OTP_LOGIN });
                    setView(isOtpSent ? OTP_CODE : SEND_OTP);
                }, style: { width: '100%', marginTop: '15px' }, id: "buttonLoginWithOtp", "data-testid": "button-login-with-otp", tabIndex: "6" }, localizedContent('loginWithOtp'))))));
};
